import React, { useState } from 'react';
import { ArrowLeft, Lock, ShieldAlert, Crown, Undo2, Search, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../lib/utils';

const AdminPointsPage = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState('');
  const [keyInput, setKeyInput] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState('');

  const [summary, setSummary] = useState(null);
  const [people, setPeople] = useState([]);
  const [feed, setFeed] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personEvents, setPersonEvents] = useState([]);
  const [error, setError] = useState(null);

  const loadAll = async (key, q = '') => {
    setError(null);
    try {
      const API_URL = getApiUrl();
      const headers = { 'X-Admin-Key': key };
      const [summaryRes, peopleRes, feedRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/points/summary`, { headers }),
        fetch(`${API_URL}/api/admin/points/people${q ? `?q=${encodeURIComponent(q)}` : ''}`, { headers }),
        fetch(`${API_URL}/api/admin/points/feed`, { headers })
      ]);
      if (summaryRes.status === 401) {
        setIsUnlocked(false);
        setAuthError('Invalid admin key. Please try again.');
        return;
      }
      setSummary(await summaryRes.json());
      setPeople(await peopleRes.json());
      setFeed(await feedRes.json());
    } catch (err) {
      setError('Failed to load points data.');
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    setAuthError('');
    setIsAuthenticating(true);
    try {
      const API_URL = getApiUrl();
      const res = await fetch(`${API_URL}/api/admin/points/summary`, { headers: { 'X-Admin-Key': keyInput } });
      if (res.status === 401) {
        setAuthError('Invalid admin key. Please try again.');
        return;
      }
      setAdminKey(keyInput);
      setIsUnlocked(true);
      loadAll(keyInput);
    } catch (err) {
      setAuthError('Could not connect. Please try again.');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const openPerson = async (email) => {
    const API_URL = getApiUrl();
    const res = await fetch(`${API_URL}/api/admin/points/people/${encodeURIComponent(email)}/events`, {
      headers: { 'X-Admin-Key': adminKey }
    });
    setPersonEvents(await res.json());
    setSelectedPerson(email);
  };

  const reverseEvent = async (eventId) => {
    const API_URL = getApiUrl();
    await fetch(`${API_URL}/api/admin/points/events/${eventId}/reverse`, {
      method: 'POST',
      headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: 'Reversed by admin' })
    });
    await openPerson(selectedPerson);
    loadAll(adminKey, searchTerm);
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Permanently delete this point event? This cannot be undone.')) return;
    const API_URL = getApiUrl();
    await fetch(`${API_URL}/api/admin/points/events/${eventId}`, {
      method: 'DELETE',
      headers: { 'X-Admin-Key': adminKey }
    });
    await openPerson(selectedPerson);
    loadAll(adminKey, searchTerm);
  };

  const deleteAllEventsForPerson = async (email) => {
    if (!window.confirm(`Permanently delete ALL point events for ${email}? This cannot be undone.`)) return;
    const API_URL = getApiUrl();
    await fetch(`${API_URL}/api/admin/points/people/${encodeURIComponent(email)}/events`, {
      method: 'DELETE',
      headers: { 'X-Admin-Key': adminKey }
    });
    setSelectedPerson(null);
    setPersonEvents([]);
    loadAll(adminKey, searchTerm);
  };

  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-2xl p-8 bg-slate-900/60 border border-slate-700/50 shadow-2xl text-center" data-testid="admin-points-locked-card">
          <div className="w-14 h-14 rounded-full bg-slate-800/70 border border-slate-700 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-slate-400" />
          </div>
          <h1 className="text-lg font-bold text-white mb-1">Guardian Points — Staff Only</h1>
          <p className="text-sm text-slate-400 mb-6">Enter the admin key to view points data.</p>
          <form onSubmit={handleUnlock} className="space-y-3">
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Admin key"
              autoComplete="off"
              data-testid="admin-points-key-input"
              className="w-full px-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-400/50"
              disabled={isAuthenticating}
            />
            {authError && (
              <div className="flex items-center gap-2 text-xs text-rose-400" data-testid="admin-points-key-error">
                <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                {authError}
              </div>
            )}
            <button
              type="submit"
              disabled={isAuthenticating || !keyInput.trim()}
              data-testid="admin-points-unlock-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-2.5 text-sm font-medium text-slate-900 hover:bg-amber-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isAuthenticating ? 'Checking...' : 'Unlock'}
            </button>
          </form>
          <button onClick={() => navigate('/admin')} className="mt-6 inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-300 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white px-4 sm:px-8 py-8">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/admin')} className="inline-flex items-center gap-2 mb-6 text-sm text-slate-400 hover:text-white transition-colors" data-testid="admin-points-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </button>

        <div className="flex items-center gap-2 mb-1">
          <Crown className="w-6 h-6 text-amber-300" />
          <h1 className="text-2xl font-bold text-white">Guardian Points</h1>
        </div>
        <p className="text-sm italic text-slate-400 mb-8" data-testid="admin-points-tagline">
          Points follow behavior. Popularity does not pay for care.
        </p>

        {error && <p className="text-sm text-rose-400 mb-4">{error}</p>}

        {/* Totals */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-points-totals">
            <div className="rounded-xl bg-slate-900/60 border border-slate-700/50 p-4">
              <div className="text-2xl font-bold">{summary.usersWithPoints}</div>
              <div className="text-xs text-slate-400">Users With Points</div>
            </div>
            <div className="rounded-xl bg-slate-900/60 border border-slate-700/50 p-4">
              <div className="text-2xl font-bold text-amber-300">{summary.foundingSeatsUsed}/{summary.foundingSeatLimit}</div>
              <div className="text-xs text-slate-400">Founding Seats Used</div>
            </div>
            <div className="rounded-xl bg-slate-900/60 border border-slate-700/50 p-4">
              <div className="text-2xl font-bold">{summary.pointsIssued}</div>
              <div className="text-xs text-slate-400">Points Issued</div>
            </div>
            <div className="rounded-xl bg-slate-900/60 border border-slate-700/50 p-4">
              <div className="text-2xl font-bold">{summary.eventCount}</div>
              <div className="text-xs text-slate-400">Event Count</div>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
          {/* People List */}
          <div className="min-w-0">
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); loadAll(adminKey, e.target.value); }}
                placeholder="Search by name, email, or pet..."
                data-testid="admin-points-search"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-400/50"
              />
            </div>
            <div className="rounded-xl bg-slate-900/40 border border-slate-700/50 overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full" data-testid="admin-points-people-table">
                <thead className="bg-slate-800/50 border-b border-slate-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">Badges</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">Balance</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-slate-400">Events</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {people.length === 0 ? (
                    <tr><td colSpan="4" className="px-4 py-8 text-center text-slate-500">No point events yet.</td></tr>
                  ) : people.map((p) => (
                    <tr key={p.email} onClick={() => openPerson(p.email)} data-testid={`admin-points-person-row-${p.email}`} className="hover:bg-slate-800/30 transition-colors cursor-pointer">
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-white">{p.displayName || '—'}</div>
                        <div className="text-xs text-slate-500">{p.email}{p.petName ? ` · ${p.petName}` : ''}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {p.isFounding && <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/10 border border-amber-400/30 px-2 py-0.5 text-amber-300"><Crown className="w-3 h-3" /> Founding</span>}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-white">{p.balance}</td>
                      <td className="px-4 py-3 text-sm text-slate-400">{p.eventCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>

            {/* Ledger */}
            {selectedPerson && (
              <div className="mt-4 rounded-xl bg-slate-900/60 border border-amber-400/20 p-4" data-testid="admin-points-ledger">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-white">Ledger — {selectedPerson}</h3>
                  <button
                    onClick={() => deleteAllEventsForPerson(selectedPerson)}
                    data-testid="admin-points-delete-all-events"
                    className="inline-flex items-center gap-1 text-xs text-rose-400 hover:text-rose-300"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete all events
                  </button>
                </div>
                <div className="space-y-2">
                  {personEvents.map((ev) => (
                    <div key={ev.id} className="flex items-center justify-between gap-3 text-sm border-b border-slate-800/50 pb-2">
                      <div>
                        <div className="text-white">{ev.activityLabel} <span className={ev.points >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{ev.points >= 0 ? '+' : ''}{ev.points}</span></div>
                        <div className="text-xs text-slate-500">{ev.detail} · {new Date(ev.createdAt).toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {ev.points > 0 && !ev.reversalOf && (
                          <button onClick={() => reverseEvent(ev.id)} data-testid={`admin-points-reverse-${ev.id}`} className="inline-flex items-center gap-1 text-xs text-rose-400 hover:text-rose-300">
                            <Undo2 className="w-3.5 h-3.5" /> Reverse
                          </button>
                        )}
                        <button onClick={() => deleteEvent(ev.id)} data-testid={`admin-points-delete-${ev.id}`} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-400">
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Feed */}
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-white mb-3">Newest Events</h3>
            <div className="rounded-xl bg-slate-900/40 border border-slate-700/50 divide-y divide-slate-800/50" data-testid="admin-points-feed">
              {feed.length === 0 ? (
                <p className="px-4 py-8 text-center text-slate-500 text-sm">No events yet.</p>
              ) : feed.map((ev) => (
                <div key={ev.id} className="px-4 py-3 text-sm">
                  <div className="text-white">{ev.displayName || ev.email} <span className={ev.points >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{ev.points >= 0 ? '+' : ''}{ev.points} pts</span></div>
                  <div className="text-xs text-slate-500">{ev.detail}</div>
                  <div className="text-[10px] text-slate-600">{new Date(ev.createdAt).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPointsPage;
